import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, AlertCircle, Star, Phone } from 'lucide-react';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { useTranslation } from '../i18n';
const WEB3FORMS_ACCESS_KEY = 'a7f2e5e4-a0ef-4a6e-a281-2d4c3076403e';
const LEAVE_REVIEW_URL = 'https://g.page/r/CUmhstMLWUqlEBM/review';
export function CallbackForm() {
  const {
    t,
    lang
  } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    privacyConsent: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const nameLabel = lang === 'en' ? 'Your name' : lang === 'ua' ? "Ваше ім'я" : lang === 'ro' ? 'Numele dumneavoastră' : 'Ваше имя';
  const phoneLabel = lang === 'en' ? 'Phone number' : lang === 'ua' ? 'Номер телефону' : lang === 'ro' ? 'Număr de telefon' : 'Номер телефона';
  const submitLabel = lang === 'en' ? 'Request a call back' : lang === 'ua' ? 'Замовити дзвінок' : lang === 'ro' ? 'Solicitați apel' : 'Заказать звонок';
  const formTitle = lang === 'en' ? 'Request a Call Back' : lang === 'ua' ? 'Замовити зворотний дзвінок' : lang === 'ro' ? 'Solicitați un apel înapoi' : 'Заказать обратный звонок';
  const formSubtitle = lang === 'en' ? 'Leave your name and phone — we will call you back shortly.' : lang === 'ua' ? "Залиште ім'я та телефон — ми зателефонуємо вам найближчим часом." : lang === 'ro' ? 'Lăsați numele și numărul de telefon — vă vom suna înapoi cât mai curând posibil.' : 'Оставьте имя и телефон — мы перезвоним вам в ближайшее время.';
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = t('callbackForm.required');
    if (!formData.phone.trim()) newErrors.phone = t('callbackForm.required');
    if (!formData.privacyConsent) newErrors.privacyConsent = t('callbackForm.required');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: 'New Callback Request — Dentico Clinic',
          from_name: 'Dentico Clinic Website',
          botcheck: '',
          name: formData.name,
          phone: formData.phone,
          required_consent: 'Yes',
          page_url: window.location.href,
          submitted_at: new Date().toLocaleString()
        })
      });
      const result = await response.json();
      if (result.success) {
        setIsSuccess(true);
        setFormData({
          name: '',
          phone: '',
          privacyConsent: false
        });
      } else {
        setError(t('callbackForm.errorMessage'));
      }
    } catch (err) {
      setError(t('callbackForm.errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSuccess) {
    return <Card className="p-8 text-center bg-ivory-200 border-beige/60" data-id="element-128" data-mp="353lwfi6ccg92">
        <div className="w-16 h-16 bg-ivory rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-129" data-mp="3a0cc44e8h9ty">
          <Check className="w-8 h-8 text-forest-600" data-id="element-130" data-mp="3e2fy3myj3wth" />
        </div>
        <h3 className="text-2xl font-bold text-charcoal mb-2" data-id="element-131" data-mp="3j1snaecvhfz8">
          {t('callbackForm.successTitle')}
        </h3>
        <p className="text-charcoal-light mb-8" data-id="element-132" data-mp="3o1f1aq0iz093">
          {t('callbackForm.successMessage')}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-133" data-mp="3sz6pb8y1u280">
          <Button onClick={() => setIsSuccess(false)} variant="outline" data-id="element-134" data-mp="1laupacpke7x">
            {lang === 'en' ? 'Send another request' : lang === 'ua' ? 'Надіслати ще один запит' : lang === 'ro' ? 'Trimiteți o altă solicitare' : 'Отправить ещё один запрос'}
          </Button>
          <a href={LEAVE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-id="element-135" data-mp="fld6qfyc4df9">
            <Button variant="primary" className="gap-2" data-id="element-136" data-mp="pptj2zclvceb">
              <Star className="w-4 h-4" data-id="element-137" data-mp="kq75gosv7u4z" />
              {t('reviews.leaveReview')}
            </Button>
          </a>
        </div>
      </Card>;
  }
  return <Card className="p-6 sm:p-8" data-id="element-138" data-mp="fr4bq0ic5acb">
      <div className="mb-6" data-id="element-139" data-mp="bpirtv7dvvsm">
        <div className="flex items-center gap-3 mb-3" data-id="element-140" data-mp="bqaprcoge5m2">
          <div className="p-2 bg-ivory-200 rounded-lg" data-id="element-141" data-mp="brlouz6yep6u">
            <Phone className="w-5 h-5 text-forest-600" data-id="element-142" data-mp="18e6yxwhk9yn9" />
          </div>
          <h2 className="text-2xl font-bold text-charcoal" data-id="element-143" data-mp="13g5dx60krp95">{formTitle}</h2>
        </div>
        <p className="text-charcoal-light" data-id="element-144" data-mp="ypgkhl5c4i7d">{formSubtitle}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" data-id="element-145" data-mp="ync8sh172ewj">
        <input type="checkbox" name="botcheck" className="hidden" style={{
        display: 'none'
      }} data-id="element-146" data-mp="unbxh59e6jtp" />

        {/* Name */}
        <div data-id="element-147" data-mp="1me87ly42iada">
          <label htmlFor="name" className="block text-sm font-medium text-charcoal mb-1" data-id="element-148" data-mp="1mdq3ygf2stvc">
            {nameLabel} <span className="text-red-500" data-id="element-149" data-mp="3gphivik9q93c">*</span>
          </label>
          <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} placeholder={lang === 'en' ? 'John' : lang === 'ua' ? 'Олена' : lang === 'ro' ? 'Maria' : 'Александр'} className={`w-full rounded-xl border-beige/60 focus:border-forest-500 focus:ring-forest-500 transition-colors text-lg py-3 ${errors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`} data-id="element-150" data-mp="3llolb1dgh2gj" />
          {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-151" data-mp="m6usxkgwk0m2">{errors.name}</p>}
        </div>

        {/* Phone */}
        <div data-id="element-152" data-mp="r4wb836i7vwh">
          <label htmlFor="phone" className="block text-sm font-medium text-charcoal mb-1" data-id="element-153" data-mp="r5nx2maauf9k">
            {phoneLabel} <span className="text-red-500" data-id="element-154" data-mp="26pf1ozjj5vsx">*</span>
          </label>
          <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="+44..." className={`w-full rounded-xl border-beige/60 focus:border-forest-500 focus:ring-forest-500 transition-colors text-lg py-3 ${errors.phone ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`} data-id="element-155" data-mp="26qyf8idsxsr0" />
          {errors.phone && <p className="mt-1 text-sm text-red-600" data-id="element-156" data-mp="34d5lrxto1v60">{errors.phone}</p>}
        </div>

        {/* Privacy Consent */}
        <div className="flex items-start gap-3 pt-2" data-id="element-157" data-mp="2zec7w4agw25b">
          <div className="flex items-center h-5" data-id="element-158" data-mp="19ymhpqth7jlv">
            <input id="privacyConsent" name="privacyConsent" type="checkbox" checked={formData.privacyConsent} onChange={handleChange} className={`h-4 w-4 rounded border-beige/60 text-forest-600 focus:ring-forest-500 ${errors.privacyConsent ? 'border-red-300' : ''}`} data-id="element-159" data-mp="19zecx0j5vfbm" />
          </div>
          <div className="text-sm" data-id="element-160" data-mp="sxqhefpd1xgs">
            <label htmlFor="privacyConsent" className="text-charcoal-light" data-id="element-161" data-mp="syibtn781p5h">
              {t('callbackForm.privacyConsent')}{' '}
              <span className="text-red-500" data-id="element-162" data-mp="89s3qx1ewos">*</span>
            </label>
            {errors.privacyConsent && <p className="mt-1 text-sm text-red-600" data-id="element-163" data-mp="561mcrl5muw5">
                {errors.privacyConsent}
              </p>}
          </div>
        </div>

        {/* Submit */}
        <Button type="submit" fullWidth size="lg" isLoading={isSubmitting} className="mt-4" data-id="element-164" data-mp="f4r66pwm2pf1">
          {submitLabel}
        </Button>

        {error && <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg text-sm" data-id="element-165" data-mp="1qjedezhwmdrs">
            <AlertCircle className="w-4 h-4 shrink-0" data-id="element-166" data-mp="3b2hx3bbrlx36" />
            <p data-id="element-167" data-mp="3b41cz0tie64f">{error}</p>
          </div>}

        <p className="text-xs text-charcoal-light text-center mt-3" data-id="element-168" data-mp="3646lav1wtcib">
          {t('callbackForm.legalText').replace('Privacy Policy', '___PRIVACY___').replace('Patient Privacy Notice', '___PATIENT___').replace('Політикою конфіденційності', '___PRIVACY___').replace('Повідомленням про конфіденційність пацієнта', '___PATIENT___').replace('Политикой конфиденциальности', '___PRIVACY___').replace('Уведомлением о конфиденциальности пациента', '___PATIENT___').replace('Politica de Confidențialitate', '___PRIVACY___').replace('Notificarea privind Confidențialitatea Pacientului', '___PATIENT___').split(/(___PRIVACY___|___PATIENT___)/).map((part, i) => {
          if (part === '___PRIVACY___') {
            return <Link key={i} to={`/${lang}/privacy-policy`} className="text-forest-600 hover:underline" data-id="element-169" data-mp="2txwebfw4fbq8">
                    {lang === 'en' ? 'Privacy Policy' : lang === 'ua' ? 'Політикою конфіденційності' : lang === 'ro' ? 'Politica de Confidențialitate' : 'Политикой конфиденциальности'}
                  </Link>;
          }
          if (part === '___PATIENT___') {
            return <Link key={i} to={`/${lang}/patient-privacy-notice`} className="text-forest-600 hover:underline" data-id="element-170" data-mp="2k575ytvvadz4">
                    {lang === 'en' ? 'Patient Privacy Notice' : lang === 'ua' ? 'Повідомленням про конфіденційність пацієнта' : lang === 'ro' ? 'Notificarea privind Confidențialitatea Pacientului' : 'Уведомлением о конфиденциальности пациента'}
                  </Link>;
          }
          return part;
        })}
        </p>
      </form>
    </Card>;
}